/** @format */

import { produce } from "immer";
import {
  cardsApi,
  ResourceCardBody,
} from "../../app/services/resource/cardsApi";
import { lessonApi } from "../../app/services/resource/lessonApi";
import { resourcesApi } from "../../app/services/resource/resourcesApi";
import { store } from "../../app/store";
import { getResourceCode } from "../../utils/GetResourceCode";
import { staticCardTypes } from "../contentEditor/utils";
import { Locale, LocaleText } from "../i18n/init";
import { UuidString } from "../userResources/userResourceFunctions";
import {
  Card,
  CardReference,
  CardType,
  Lesson,
  Resource,
  ResourceObject,
  Sublesson,
} from "./types";

type FullSublesson = Omit<Sublesson, "cards"> & { cards: Card[] };
type FullLesson = Omit<Lesson, "subLessons"> & { subLessons: FullSublesson[] };
type FullResourceObject = Omit<ResourceObject, "lessons"> & {
  lessons: FullLesson[];
};
export type FullResource = Omit<Resource, "Object"> & {
  Object: FullResourceObject;
};

/** Check if value is in the format of a UUID (Universally unique identifier) */
export const isValidUuid = (uuid: string) => {
  const regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(uuid.toLowerCase());
};

/** Generate a random UUID using
 *  @see https://nodejs.org/api/crypto.html#cryptorandomuuidoptions
 */
export const getRandomUUID = () => crypto.randomUUID() as UuidString;

export const checkSublesson = (
  sublesson: Omit<Sublesson, "uuid"> & { uuid?: string },
  locale: Locale,
): Omit<Sublesson, "title"> & { title: Partial<LocaleText> } => {
  let _sublesson = { ...sublesson };

  const emptyLocaleText: Partial<LocaleText> = {
    en: "",
    cy: "",
  };

  _sublesson.title =
    typeof _sublesson.title === "object"
      ? _sublesson.title
      : { ...emptyLocaleText, [locale]: _sublesson.title };

  if (!_sublesson.uuid) {
    _sublesson.uuid = getRandomUUID();
  }

  return _sublesson as Omit<Sublesson, "title"> & {
    title: Partial<LocaleText>;
  };
};

/** Function to check if lesson has a UUID, generating one if it doesn't exist
 *  @returns { Lesson } lesson with UUID.
 */
export const checkLesson = (
  lesson: Omit<Lesson, "uuid"> & { uuid?: string },
): Lesson => {
  let _lesson = { ...lesson };
  if (!_lesson.uuid) {
    _lesson.uuid = getRandomUUID();
  }

  return _lesson as Lesson;
};

/** Function to add new card with template data
 *  @param { CardType } type - Type of card to be created.
 *  @param { Resource } resource - Resource to add card to.
 *  @param { number } lessonIndex - Lesson to add card to.
 *  @param { number } sublessonIndex - Sublesson to add card to.
 *  @param { number } newIndex - Card's new index in sublesson.
 * @returns saved card's UUID
 */
export const addCard = async (
  type: CardType,
  resource: Resource,
  lessonIndex: number,
  sublessonIndex: number,
  newIndex: number,
): Promise<string> => {
  const sublesson =
    resource.Object.lessons[lessonIndex].subLessons[sublessonIndex];

  const _staticCardTypes = staticCardTypes.filter(
    (type) => !["gallery", "carousel"].includes(type.id),
  );

  // Fetch example.json from activity or read card
  const data = await import(
    `../activityCards/cards/${
      _staticCardTypes.find((_type) => type === _type.id)
        ? "Static"
        : type.charAt(0).toUpperCase() + type.slice(1)
    }Card/template.json`
  )
    .then((json) => {
      return json.data;
    })
    .catch(() => {
      throw Error("Unable to retrieve correct data");
    });

  const card: Omit<Card, "UUID" | "PK" | "SK" | "created" | "modified"> & {
    UUID?: string;
    PK?: string;
    SK?: string;
    created?: string;
    modified?: string;
  } = {
    Object: {
      type: type,
      data: data,
    },
    Parent: sublesson.uuid,
  };

  const _card = checkCard(card, getResourceCode(resource), sublesson.uuid);
  return await saveCard(_card, resource, lessonIndex, sublessonIndex, newIndex);
};

/**
 * @returns saved card's UUID
 */
export const saveCard = async (
  card: Card,
  resource: Resource,
  lessonIndex: number,
  sublessonIndex: number,
  newIndex?: number,
) => {
  const dispatch = store.dispatch;
  const sublesson =
    resource.Object.lessons[lessonIndex].subLessons[sublessonIndex];
  const _card = card;

  const body: { resourceCardItem: ResourceCardBody } = {
    resourceCardItem: {
      Object: _card.Object,
      PK: _card.PK,
      SK: _card.SK,
      Parent: sublesson.uuid,
      UUID: _card.UUID,
      Code: getResourceCode(resource),
    },
  };

  // save card as ResourceCard
  const savedCard = await dispatch(
    cardsApi.endpoints.saveResourceCard.initiate(body),
  ).unwrap();

  // Add card reference if the card saved is new
  if (savedCard.newCard) {
    const selectedCardIndex = (
      resource.Object?.lessons?.[lessonIndex]?.subLessons?.[sublessonIndex]
        ?.cards ?? []
    ).findIndex((item) => item.uuid === _card.UUID);

    const newCardReference: CardReference = {
      type: _card.Object.type,
      uuid: _card.UUID,
    };

    let newCards: CardReference[] = [
      ...(resource.Object?.lessons?.[lessonIndex]?.subLessons?.[sublessonIndex]
        ?.cards ?? []),
    ];

    if (selectedCardIndex >= 0) {
      newCards.splice(selectedCardIndex, 1, newCardReference);
    } else {
      if (newIndex !== undefined) {
        newCards.splice(newIndex, 0, newCardReference);
      }
    }

    let newResource = produce(resource, (draft) => {
      draft.Object.lessons[lessonIndex].subLessons[sublessonIndex] = {
        ...sublesson,
        cards: newCards,
      };
    });

    try {
      await dispatch(
        lessonApi.endpoints.updateLesson.initiate({
          primaryKey: {
            PK: resource.PK,
            SK: resource.SK,
          },
          diff: { Object: { lessons: newResource.Object.lessons } },
        }),
      ).catch((error) => {
        console.error(error);
        throw new Error(error);
      });
    } catch {
      console.log("Failed");
    }
  }

  return _card.UUID;
};

export const generateUserCardSK = (
  subLessonUuid: UuidString,
  cardUuid: UuidString,
): string => `CARD#PARENT:${subLessonUuid}#UUID:${cardUuid}`;
export const generateUserCardPK = (code: string): string =>
  `BL#${code.toUpperCase()}`;

/** Check if card is a reference to a resource or an original card of a UserResource */
export const checkCard = (
  card: Omit<Card, "UUID" | "PK" | "SK" | "created" | "modified"> & {
    UUID?: string;
    PK?: string;
    SK?: string;
    created?: string;
    modified?: string;
  },
  code: string,
  parent: string,
): Card => {
  let _card = { ...card };

  _card.UUID = getRandomUUID();
  _card.PK = generateUserCardPK(code);
  _card.SK = generateUserCardSK(parent as UuidString, _card.UUID as UuidString);
  return _card as Card;
};

export const moveItemUp = (
  currItems: (CardReference | Lesson)[],
  selectedItemIndex: number,
) => {
  let newItems: (CardReference | Lesson)[] = [...currItems];
  let currentItem: CardReference | Lesson = currItems[selectedItemIndex];

  if (selectedItemIndex > 0) {
    newItems.splice(selectedItemIndex, 1);
    newItems.splice(selectedItemIndex - 1, 0, currentItem);
  } else {
    return undefined;
  }

  return newItems;
};

export const moveItemDown = (
  currItems: (CardReference | Lesson)[],
  selectedCardIndex: number,
) => {
  let newItems: (CardReference | Lesson)[] = [...currItems];
  let currentItem: CardReference | Lesson = currItems[selectedCardIndex];
  const maxLength = currItems.length;

  if (selectedCardIndex < maxLength - 1) {
    newItems.splice(selectedCardIndex, 1);
    newItems.splice(selectedCardIndex + 1, 0, currentItem);
  } else {
    return undefined;
  }

  return newItems;
};

export const moveCard = async (
  resourceId: string,
  lessonIndex: number,
  sublessonIndex: number,
  itemUUID: string,
  action: "move-up" | "move-down",
  locale: Locale,
) => {
  const dispatch = store.dispatch;

  const { data: resource } = await dispatch(
    resourcesApi.endpoints.getResource.initiate(resourceId),
  );

  if (resource) {
    const selectedCardIndex = resource.Object.lessons[lessonIndex].subLessons[
      sublessonIndex
    ].cards.findIndex((item) => item.uuid === itemUUID);
    let currentCards: CardReference[] = [
      ...resource.Object.lessons[lessonIndex].subLessons[sublessonIndex].cards,
    ];

    let newCards: CardReference[] | undefined;

    if (action === "move-up") {
      newCards = moveItemUp(currentCards, selectedCardIndex) as
        | CardReference[]
        | undefined;
    }

    if (action === "move-down") {
      newCards = moveItemDown(currentCards, selectedCardIndex) as
        | CardReference[]
        | undefined;
    }

    if (newCards !== undefined) {
      let newResource = produce(resource, (draft) => {
        let sublesson =
          draft.Object.lessons[lessonIndex].subLessons[sublessonIndex];
        draft.Object.lessons[lessonIndex].subLessons[sublessonIndex] =
          checkSublesson(
            { ...sublesson, cards: newCards ?? currentCards },
            locale,
          );
      });

      try {
        await dispatch(
          lessonApi.endpoints.updateLesson.initiate({
            primaryKey: {
              PK: resource.PK,
              SK: resource.SK,
            },
            diff: { Object: { lessons: newResource.Object.lessons } },
          }),
        );
      } catch (e) {
        console.error(e);
      }
    }
  }
};

export const moveLesson = async (
  resourceId: string,
  action: "move-up" | "move-down",
  itemUUID: string,
) => {
  const dispatch = store.dispatch;

  const { data: resource } = await dispatch(
    resourcesApi.endpoints.getResource.initiate(resourceId),
  );

  if (resource) {
    const selectedLessonIndex = resource.Object.lessons.findIndex(
      (item) => item.uuid === itemUUID,
    );
    let currentLessons: Lesson[] = [...resource.Object.lessons];

    let newLessons: Lesson[] | undefined;

    if (action === "move-up") {
      newLessons = moveItemUp(currentLessons, selectedLessonIndex) as
        | Lesson[]
        | undefined;
    }

    if (action === "move-down") {
      newLessons = moveItemDown(currentLessons, selectedLessonIndex) as
        | Lesson[]
        | undefined;
    }

    if (newLessons !== undefined) {
      try {
        await dispatch(
          resourcesApi.endpoints.updateResource.initiate({
            diff: {
              Object: {
                lessons: newLessons ?? currentLessons,
              },
            },
            primaryKey: {
              PK: resource.PK,
              SK: resource.SK,
            },
          }),
        );
        // console.log(updatedResource)
      } catch {
        // console.log("Failed");
      }
    }
  }
};
